import { Organization, OrganizationSection } from "../domain/organizations/organization.types";
import { Asset } from "../domain/shared/asset.types";
import { ContactModule, SocialLinks } from "../domain/shared/contact.types";
import { LocalizedText } from "../domain/shared/localized-text.types";
import type { Association } from "../legacy/types/legacyBusiness.types";

export interface LegacyAssociationContent {
  slogan: LocalizedText;
  description: LocalizedText;
  history: LocalizedText;
  mission: LocalizedText;
  vision: LocalizedText;
  galleryCaption: LocalizedText;
}

const nonEmpty = (value?: string): string | undefined => {
  const trimmed = value?.trim();
  return trimmed ? trimmed : undefined;
};

const spanishText = (value?: string): LocalizedText | undefined => {
  const text = nonEmpty(value);
  return text ? { es: text } : undefined;
};

const toSocialLinks = (source: Association["socialMedia"]): SocialLinks | undefined => {
  const social: SocialLinks = {
    ...(nonEmpty(source.facebook) ? { facebook: nonEmpty(source.facebook) } : {}),
    ...(nonEmpty(source.instagram) ? { instagram: nonEmpty(source.instagram) } : {}),
    ...(nonEmpty(source.tiktok) ? { tiktok: nonEmpty(source.tiktok) } : {}),
    ...(nonEmpty(source.twitter) ? { twitter: nonEmpty(source.twitter) } : {}),
    ...(nonEmpty(source.whatsapp) ? { whatsapp: nonEmpty(source.whatsapp) } : {}),
    ...(nonEmpty(source.website) ? { website: nonEmpty(source.website) } : {}),
  };
  return Object.values(social).some(Boolean) ? social : undefined;
};

const toContact = (source: Association): ContactModule | undefined => {
  const email = nonEmpty(source.contactInfo.email);
  const phone = nonEmpty(source.contactInfo.phone);
  const address = spanishText(source.contactInfo.address);
  const social = toSocialLinks(source.socialMedia);
  if (!email && !phone && !address && !social) return undefined;

  return {
    emails: email ? [email] : undefined,
    phones: phone ? [phone] : undefined,
    address,
    social,
  };
};

export const adaptLegacyAssociation = (
  source: Association,
  content: LegacyAssociationContent
): Organization => {
  const sourceName = nonEmpty(source.name);
  if (!sourceName) throw new Error("Missing indispensable name for legacy association");

  const name: LocalizedText = { es: sourceName, en: sourceName };
  const address = spanishText(source.contactInfo.address);
  const contact = toContact(source);
  const images: Asset[] = source.galleryImages.map((src) => ({
    src,
    alt: name,
    caption: content.galleryCaption,
  }));
  const sections: OrganizationSection[] = [
    { type: "about", enabled: true },
    { type: "institutional", enabled: true },
  ];
  if (images.length) sections.push({ type: "gallery", enabled: true });
  if (contact) sections.push({ type: "contact", enabled: true });

  return {
    id: "apinativos",
    kind: "institutional",
    identity: {
      name,
      summary: content.slogan,
      description: content.description,
      logo: { src: source.logo, alt: name },
    },
    institutional: {
      history: content.history,
      mission: content.mission,
      vision: content.vision,
    },
    contact,
    location: address ? { address } : undefined,
    media: images.length ? { images } : undefined,
    sections,
  };
};
